import React, { useCallback, useEffect, useRef, useState } from "react";
import { Drawer, IconButton, Tabs, Tab, Box, Typography, Chip, Accordion, AccordionSummary, AccordionDetails, Skeleton } from "@mui/material";
import { X, ChevronDown as ChevronDownIcon, Trash2, Paperclip, Download } from "lucide-react";
import { showSuccessModal } from "./ui/ConfirmDialog";
import axios from "../axiosConfig";
import { fetchProductFamilies, fetchProductLines, fetchBusinessUnits } from "../services/filtersService";
import AvatarImage from "./AvatarImage";
import UserSelector from "./UserSelector";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { logSystemError } from "../services/usageService";

export interface DetailsDrawerProps {
	open: boolean;
	onClose: () => void;
    // Primary key to load details
	recordId?: string;
	// Create mode
	isCreate?: boolean;
	onSaved?: () => void;
	onOpenConfigurator: () => void;
    // If true, open the drawer directly in edit mode (viewing existing)
    startInEditMode?: boolean;
    // If true (default), show the "Open" button that navigates to the details page
    showOpenButton?: boolean;
}

interface CodeIndexFormValues {
	partNumber: string;
	productFamily: string;
	productLine: string;
	format: string;
	pcl: string;
	businessUnit: string;
	description: string;
	stackMembers: string[];
}

export default function CodeIndexDetailsDrawer(props: DetailsDrawerProps) {
    const { open, onClose, recordId, isCreate, onSaved, startInEditMode, showOpenButton = true } = props;

	const navigate = useNavigate();

	const [activeTab, setActiveTab] = useState(0);
	const [isEditing, setIsEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [uploadingImage, setUploadingImage] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [descriptionExpanded, setDescriptionExpanded] = useState(false);

	// Validation schema
	const validationSchema = yup.object().shape({
		partNumber: yup
			.string()
			.required('Part Number is required')
			.matches(/^[a-zA-Z0-9]+$/, 'Part Number must contain only letters and numbers'),
		productFamily: yup
			.string()
			.defined(),
		productLine: yup
			.string()
			.defined(),
		format: yup
			.string()
			.required('Format number is required')
			.matches(/^[0-9]+$/, { message: 'Format must contain only numbers', excludeEmptyString: true })
			.length(3, 'Format number must be exactly 3 digits'),
		pcl: yup
			.string()
			.defined(),
		businessUnit: yup
			.string()
			.defined(),
		description: yup
			.string()
			.defined(),
		stackMembers: yup
			.array()
			.of(yup.string().defined())
			.defined(),
	});

	// React Hook Form
    const { register, handleSubmit, formState: { errors }, setValue, reset, watch, getValues } = useForm<CodeIndexFormValues>({
		resolver: yupResolver(validationSchema),
		mode: 'onBlur',
		reValidateMode: 'onBlur',
		defaultValues: {
			partNumber: '',
			productFamily: '',
			productLine: '',
			format: '',
			pcl: '',
			businessUnit: '',
			description: '',
			stackMembers: [],
		},
	});

	// Editable state (keeping only non-form states)
	const [editName, setEditName] = useState<string>("");
	const [existingImageIdName, setExistingImageIdName] = useState<string | null>(null);
	const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
const [displayImageUrl, setDisplayImageUrl] = useState<string | null>(null);
const [isImageFetching, setIsImageFetching] = useState(false);

	// Options loaded independently (blank queries)
	const [businessUnitOptions, setBusinessUnitOptions] = useState<string[]>([]);
	const [familyOptions, setFamilyOptions] = useState<string[]>([]);
	const [lineOptions, setLineOptions] = useState<string[]>([]);
const [businessUnitsLoading, setBusinessUnitsLoading] = useState(false);
const [dependentFiltersLoading, setDependentFiltersLoading] = useState(false);
const [isPreparingEditSession, setIsPreparingEditSession] = useState(false);

// Local ref for file input to show an Upload button
const fileInputRef = useRef<HTMLInputElement | null>(null);
const attachmentsInputRef = useRef<HTMLInputElement | null>(null);
const [details, setDetails] = useState<any | null>(null);
const [detailsLoading, setDetailsLoading] = useState(false);
// Stack members selection state (edit/create)
const [stackUserOptions, setStackUserOptions] = useState<Array<{ id: string; userId?: string; name: string; emailAddress: string; avatarUri?: string | null }>>([]);
const [selectedStackIds, setSelectedStackIds] = useState<string[]>([]);
const [stackUsersLoading, setStackUsersLoading] = useState(false);
const [stackUsersError, setStackUsersError] = useState<string | null>(null);
const stackUsersRequestRef = useRef(0);
const businessUnitsRequestRef = useRef(0);
const dependentFiltersRequestRef = useRef(0);

// Fetch details when opened or id changes (view mode)
useEffect(() => {
    if (!open || !recordId || isCreate) return;
    let cancelled = false;
    (async () => {
        setDetailsLoading(true);
        try {
            const { data } = await axios.get(`/CodeIndexes/${encodeURIComponent(recordId as string)}`);
            if (!cancelled) setDetails(data || null);
        } catch (_e) {
            logSystemError(null, {
                action: 'load_codesheet_metadata',
                error: _e,
                resource: 'codesheet',
                resourceId: recordId,
            });
            if (!cancelled) setDetails(null);
        } finally {
            if (!cancelled) setDetailsLoading(false);
        }
    })();
    return () => { cancelled = true; };
}, [open, recordId, isCreate]);

    // Reset stale content when closing or switching record
    useEffect(() => {
        if (!open) {
            stackUsersRequestRef.current += 1;
            businessUnitsRequestRef.current += 1;
            dependentFiltersRequestRef.current += 1;
            setDetails(null);
            setPreviewImageUrl(null);
            setDisplayImageUrl(null);
            setExistingImageIdName(null);
            setEditAttachments([]);
            setSelectedStackIds([]);
            setStackUserOptions([]);
            setStackUsersLoading(false);
            setStackUsersError(null);
            setIsImageFetching(false);
            setDetailsLoading(false);
            setBusinessUnitsLoading(false);
            setDependentFiltersLoading(false);
            setIsPreparingEditSession(false);
            setIsEditing(false);
            setBusinessUnitOptions([]);
            setFamilyOptions([]);
            setLineOptions([]);
            reset(); // Reset React Hook Form
            return;
        }
        // When switching to a different record, clear previous visuals immediately
        dependentFiltersRequestRef.current += 1;
        setPreviewImageUrl(null);
        setDisplayImageUrl(null);
        setExistingImageIdName(null);
        setEditAttachments([]);
        setIsImageFetching(false);
        setDetailsLoading(!Boolean(isCreate) && Boolean(recordId));
        setBusinessUnitsLoading(Boolean(isCreate) || Boolean(startInEditMode));
        setDependentFiltersLoading(Boolean(isCreate) || Boolean(startInEditMode));
        setStackUsersLoading(Boolean(isCreate) || Boolean(startInEditMode));
        setIsPreparingEditSession(Boolean(isCreate) || Boolean(startInEditMode));
        setFamilyOptions([]);
        setLineOptions([]);
    }, [open, recordId, reset, isCreate, startInEditMode]);

useEffect(() => {
    if (!open) return;
    setIsEditing(Boolean(isCreate) || Boolean(startInEditMode));
}, [open, isCreate, startInEditMode, recordId]);

// Sync form state whenever drawer data is ready
useEffect(() => {
    if (!open) return;
    if (!isCreate && detailsLoading) return;

    const src: any = details || {};
    setSaving(false);
    setError(null);
    setUploadingImage(false);
    setUploadError(null);
    setEditName(src?.name || "");
    setExistingImageIdName(src?.image || null);
    setPreviewImageUrl(null);
    setDisplayImageUrl(null);
    setEditAttachments(Array.isArray(src?.attachments) ? src.attachments : []);

    const initialStackIds: string[] = Array.isArray(src?.stacked)
        ? src.stacked.map((m: any) => (m?.id || '').trim()).filter(Boolean)
        : [];

    reset({
        partNumber: src?.partNumber || '',
        productFamily: src?.productFamily || '',
        productLine: src?.productLine || '',
        format: src?.format_Id || '',
        pcl: src?.pcl || '',
        businessUnit: src?.businessUnit || '',
        description: src?.description || '',
        stackMembers: initialStackIds,
    });

    setSelectedStackIds(initialStackIds);

    // Keep description accordion expanded to show validation errors
    setDescriptionExpanded(true);
}, [open, isCreate, detailsLoading, details, reset]);

	// Revoke preview URL whenever it changes or component unmounts
	useEffect(() => {
		return () => {
			if (previewImageUrl) URL.revokeObjectURL(previewImageUrl);
		};
	}, [previewImageUrl]);

const selectedBusinessUnit = watch('businessUnit');
const selectedProductFamily = watch('productFamily');
const selectedProductLine = watch('productLine');
const isEditMode = isEditing || Boolean(isCreate);
const isEditInitializationLoading = isEditMode && isPreparingEditSession;

useEffect(() => {
    if (!open || !isEditMode) return;

    const stillLoading = (!isCreate && detailsLoading) || businessUnitsLoading || dependentFiltersLoading || stackUsersLoading;
    if (!stillLoading) {
        setIsPreparingEditSession(false);
    }
}, [open, isEditMode, isCreate, detailsLoading, businessUnitsLoading, dependentFiltersLoading, stackUsersLoading]);

// Load business units for create/edit flows
useEffect(() => {
    if (!open || !isEditing) return;
    const requestId = ++businessUnitsRequestRef.current;
    let alive = true;

    (async () => {
        setBusinessUnitsLoading(true);
        try {
            const units = await fetchBusinessUnits();
            if (!alive || requestId !== businessUnitsRequestRef.current) return;
            const nextUnits = Array.isArray(units) ? units : [];
            setBusinessUnitOptions(nextUnits);

            const currentBusinessUnit = (getValues('businessUnit') || '').trim();
            if (currentBusinessUnit && !nextUnits.includes(currentBusinessUnit)) {
                setValue('businessUnit', '');
                return;
            }

            if (isCreate && !currentBusinessUnit && nextUnits.length > 0) {
                setValue('businessUnit', nextUnits[0]);
            }
        } finally {
            if (alive && requestId === businessUnitsRequestRef.current) {
                setBusinessUnitsLoading(false);
            }
        }
    })();

    return () => { alive = false; };
}, [open, isEditing, isCreate, getValues, setValue]);

// Load dependent catalogs in sequence so stale responses cannot prune newer selections.
useEffect(() => {
    if (!open || !isEditing) return;
    if (!isCreate && detailsLoading) return;

    const requestId = ++dependentFiltersRequestRef.current;
    let alive = true;

    (async () => {
        setDependentFiltersLoading(true);
        try {
            const currentBusinessUnit = (getValues('businessUnit') || '').trim();
            const fallbackBusinessUnit = !isCreate && isPreparingEditSession
                ? String(details?.businessUnit || '').trim()
                : '';
            const safeBusinessUnit = currentBusinessUnit || fallbackBusinessUnit;

            if (!safeBusinessUnit) {
                setFamilyOptions([]);
                setLineOptions([]);
                return;
            }

            const families = await fetchProductFamilies(safeBusinessUnit);
            if (!alive || requestId !== dependentFiltersRequestRef.current) return;

            const familiesList = Array.isArray(families) ? families : [];
            setFamilyOptions(familiesList);

            const currentProductFamily = (getValues('productFamily') || '').trim();
            const fallbackProductFamily = !isCreate && isPreparingEditSession
                ? String(details?.productFamily || '').trim()
                : '';
            const safeProductFamily = currentProductFamily || fallbackProductFamily;

            if (!safeProductFamily) {
                setLineOptions([]);
                return;
            }

            if (!familiesList.includes(safeProductFamily)) {
                setValue('productFamily', '');
                setValue('productLine', '');
                setLineOptions([]);
                return;
            }

            if (safeProductFamily !== currentProductFamily) {
                setValue('productFamily', safeProductFamily);
            }

            const lines = await fetchProductLines(safeProductFamily);
            if (!alive || requestId !== dependentFiltersRequestRef.current) return;

            const linesList = Array.isArray(lines) ? lines : [];
            setLineOptions(linesList);

            const currentProductLine = (getValues('productLine') || '').trim();
            const fallbackProductLine = !isCreate && isPreparingEditSession
                ? String(details?.productLine || '').trim()
                : '';
            const requestedProductLine = currentProductLine || fallbackProductLine;

            if (requestedProductLine && !linesList.includes(requestedProductLine)) {
                setValue('productLine', '');
            }

            const safeProductLine = requestedProductLine && linesList.includes(requestedProductLine)
                ? requestedProductLine
                : null;

            if (safeProductLine && safeProductLine !== currentProductLine) {
                setValue('productLine', safeProductLine);
            }
        } finally {
            if (alive && requestId === dependentFiltersRequestRef.current) {
                setDependentFiltersLoading(false);
            }
        }
    })();

    return () => { alive = false; };
}, [open, isEditing, isCreate, detailsLoading, isPreparingEditSession, details, selectedBusinessUnit, selectedProductFamily, selectedProductLine, getValues, setValue]);

const loadStackUsers = useCallback(async () => {
    const requestId = ++stackUsersRequestRef.current;
    setStackUsersLoading(true);
    setStackUsersError(null);

    try {
        const { data } = await axios.get(`/Users`);
        const usersList: Array<{ id: string; userId?: string; name: string; emailAddress: string; avatarUri?: string | null }> = Array.isArray((data as any)?.results)
            ? ((data as any).results as Array<{ id?: string; userId?: string; name?: string; emailAddress?: string; avatarUri?: string | null }>)
                .map(u => ({
                    id: String(u?.id || '').trim(),
                    userId: u?.userId ? String(u.userId) : undefined,
                    name: (u?.name || '').trim(),
                    emailAddress: (u?.emailAddress || '').trim(),
                    avatarUri: u?.avatarUri ?? null,
                }))
                .filter(u => Boolean(u.id) && Boolean(u.name) && Boolean(u.emailAddress))
            : [];
        if (requestId !== stackUsersRequestRef.current) return;
        setStackUserOptions(usersList);
        } catch (_e) {
            logSystemError(null, {
                action: 'load_stack_members',
                error: _e,
                resource: 'codesheet',
                resourceId: recordId,
            });
        if (requestId !== stackUsersRequestRef.current) return;
        setStackUserOptions([]);
        setStackUsersError('Could not load stack members. Please try again.');
    } finally {
        if (requestId === stackUsersRequestRef.current) {
            setStackUsersLoading(false);
        }
    }
}, [recordId]);

// Reload users each time the drawer opens in edit/create mode.
useEffect(() => {
    if (!open || !isEditing) return;
    void loadStackUsers();
    return () => {
        stackUsersRequestRef.current += 1;
    };
}, [open, isEditing, loadStackUsers]);


// Handle stack user selection change
const handleStackUserSelectionChange = (userIds: string[]) => {
    setSelectedStackIds(userIds);
    setValue('stackMembers', userIds);
};


// removed unused getInitials helper after introducing avatar components

// Resolve image to display: show loader while details are loading or while an image is pending fetch
const hasDeclaredImage = Boolean((existingImageIdName || details?.image) && String((existingImageIdName || details?.image)).trim());
const isImageLoading = (
    isImageFetching ||
    (!isCreate && detailsLoading && !previewImageUrl && !displayImageUrl) ||
    (hasDeclaredImage && !previewImageUrl && !displayImageUrl)
);

useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    async function load() {
        if (!open) return;
        if (previewImageUrl) return; // already showing preview
        const filename: string | null | undefined = existingImageIdName || details?.image;
        if (!filename) return;
        try {
            setIsImageFetching(true);
            const { data } = await axios.get(`Image/${encodeURIComponent(filename)}`, { responseType: 'blob' });
            if (cancelled) return;
            objectUrl = URL.createObjectURL(data);
            setDisplayImageUrl(objectUrl);
        } catch (_e) {
            logSystemError(null, {
                action: 'load_codesheet_image',
                error: _e,
                resource: 'codesheet',
                resourceId: recordId,
                context: { filename },
            });
            if (cancelled) return;
            setDisplayImageUrl(null);
        } finally {
            if (cancelled) return;
            setIsImageFetching(false);
        }
    }
    load();
    return () => {
        cancelled = true;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
}, [open, previewImageUrl, existingImageIdName, details?.image, recordId]);

	// Attachments state
const [editAttachments, setEditAttachments] = useState<string[]>([]);
	const [uploadingAttachments, setUploadingAttachments] = useState(false);
	const [attachmentsError, setAttachmentsError] = useState<string | null>(null);

	async function uploadImageImmediate(file: File) {
		setUploadingImage(true);
		setUploadError(null);
		try {
			const form = new FormData();
			form.append('file', file);
			const { data } = await axios.post(`/Image`, form);
			const idOrName = typeof data === 'string' ? data : (data?.fileName || data?.id || data?.name || null);
			if (!idOrName) throw new Error('Invalid upload response');
			setExistingImageIdName(idOrName);
		} catch (e: any) {
			logSystemError(null, {
				action: 'upload_codesheet_image',
				error: e,
				resource: 'codesheet',
				resourceId: recordId,
				context: { fileName: file.name, fileSize: file.size },
			});
			setUploadError('Image upload failed');
		} finally {
			setUploadingImage(false);
		}
	}

	// Handle Part Number change with filtering
const handlePartNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Filter out non-alphanumeric characters
    const filteredValue = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
    setValue('partNumber', filteredValue);
};

	const onSubmit = async (formData: CodeIndexFormValues) => {
		setSaving(true);
		setError(null);

		try {
			// Image was already uploaded on selection and identifier stored in existingImageIdName
			const newImageIdName = existingImageIdName || "";

			const body: any = {
				name: editName || "",
				format_Id: formData.format || "",
				partNumber: formData.partNumber || "",
				productFamily: formData.productFamily || "",
				productLine: formData.productLine || "",
				pcl: formData.pcl || "",
				businessUnit: formData.businessUnit || "",
				userId: details?.createdBy?.userId || details?.userId || "",
				image: newImageIdName,
				description: formData.description || "",
				codeItemsCount: details?.codeItemsCount ?? 0,
				codeStringLength: details?.codeStringLength ?? 0,
				attachments: editAttachments || [],
				id: recordId || "",
			};

			// Map selected emails to stacked member objects expected by API
			const stackedMembers = (formData.stackMembers || []).map((memberId: string) => {
				const found = stackUserOptions.find(u => u.id === memberId);
				return {
					id: found?.id || memberId,
					userId: found?.userId ?? null,
					name: (found?.name || '').trim(),
					emailAddress: (found?.emailAddress || '').trim(),
					avatarUri: found?.avatarUri ?? null,
					createdAt: null,
					updatedAt: null,
				};
			});
			body.stacked = stackedMembers;

      if (isCreate) {
        body.createdAt = null;
        body.updatedAt = null;
        await axios.post(`/CodeIndexes`, body);
      } else {
        await axios.put(`/CodeIndexes/${encodeURIComponent(recordId as string)}`, body);
      }
            setIsEditing(false);
            // Reload details to reflect latest data in drawer
            if (!isCreate && recordId) {
              try {
                const { data } = await axios.get(`/CodeIndexes/${encodeURIComponent(recordId as string)}`);
                setDetails(data || null);
                // Force image refresh immediately after saving, using latest filename
                const latestImage: string | null = (existingImageIdName || data?.image || null) ? String(existingImageIdName || data?.image) : null;
                setPreviewImageUrl(null);
                setDisplayImageUrl(null);
                if (latestImage) {
                  try {
                    setIsImageFetching(true);
                    const resp = await axios.get(`Image/${encodeURIComponent(latestImage)}`, { responseType: 'blob' });
                    const objUrl = URL.createObjectURL(resp.data);
                    setDisplayImageUrl(objUrl);
                  } catch (_imgErr) {
                    logSystemError(null, {
                      action: 'reload_codesheet_image_after_save',
                      error: _imgErr,
                      resource: 'codesheet',
                      resourceId: recordId,
                      context: { filename: latestImage },
                    });
                    setDisplayImageUrl(null);
                  } finally {
                    setIsImageFetching(false);
                  }
                }
              } catch (_e) {
                logSystemError(null, {
                  action: 'reload_codesheet_metadata_after_save',
                  error: _e,
                  resource: 'codesheet',
                  resourceId: recordId,
                });
                // ignore
              }
            }
            if (typeof onSaved === 'function') onSaved();
            await showSuccessModal({ title: isCreate ? 'Created successfully' : 'Updated successfully', timer: 1200, showConfirmButton: false });
		} catch (e: any) {
			logSystemError(null, {
				action: isCreate ? 'create_codesheet' : 'update_codesheet',
				error: e,
				resource: 'codesheet',
				resourceId: recordId,
			});
			setError('Failed to save changes');
		} finally {
			setSaving(false);
		}
	}

	async function uploadAttachment(file: File) {
		const form = new FormData();
		form.append('file', file);
		const { data } = await axios.post(`/File`, form);
		const idOrName = typeof data === 'string' ? data : (data?.fileName || data?.id || data?.name || null);
		if (!idOrName) throw new Error('Invalid upload response');
		return String(idOrName);
	}

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			sx={{ '& .MuiDrawer-paper': { width: '34vw', minWidth: 360, maxWidth: 560, boxSizing: 'border-box' } }}
		>
			<div className="h-full flex flex-col bg-white">
				<div className="bg-[#E3F2FD] h-1"></div>
				<div className="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200">
					<Typography variant="h5" className="font-semibold text-gray-900">
					{isCreate ? 'New Code Sheet' : 'Details'}
					</Typography>
				<div className="flex items-center gap-1">
				<IconButton onClick={onClose} className="text-gray-500 hover:text-gray-700">
					<X className="h-5 w-5" />
				</IconButton>
				</div>
				</div>

				<Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'white', px: 2 }}>
					<Tabs
						value={activeTab}
						onChange={(_, v) => setActiveTab(v)}
							sx={{
							'& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '13px', color: '#6B7280', minHeight: '44px', padding: '8px 16px' },
							'& .Mui-selected': { color: '#1D5176', fontWeight: 600 },
							'& .MuiTabs-indicator': { backgroundColor: '#1D5176', height: '2px' },
						}}
					>
						<Tab label="Summary" />
						<Tab label="Changelog" />
					</Tabs>
				</Box>

				<div className="relative flex-1 overflow-auto p-4 bg-white">
					{isEditInitializationLoading && (
						<div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75 backdrop-blur-[1px]">
								<div className="mx-6 w-full max-w-sm rounded-xl border border-gray-200 bg-white px-4 py-5 shadow-sm">
									<div className="flex items-center gap-3">
										<div className="h-8 w-8 min-h-8 min-w-8 shrink-0 rounded-full border-4 border-gray-300 border-t-[#1D5176] animate-spin" aria-label="Loading edit form" />
										<div>
											<div className="text-sm font-semibold text-gray-900">Preparing edit form</div>
											<div className="text-xs text-gray-600">
											Loading details, dropdown options and stack members before enabling edits.
										</div>
									</div>
								</div>
							</div>
						</div>
					)}
					{activeTab === 0 && (
						<div className="space-y-4">
						{/* Current Owner */}
						<div>
							<Typography variant="subtitle2" className="text-gray-600 mb-2 text-sm font-medium">Current Owner</Typography>
                            <div className="flex items-center gap-3">
								{detailsLoading ? (
									<>
										<Skeleton variant="circular" width={36} height={36} />
										<Skeleton variant="text" width={140} height={16} />
									</>
								) : (
									<>
                                        <AvatarImage name={details?.createdBy?.name || '—'} filename={details?.createdBy?.avatarUri || null} size={36} />
										<Typography variant="body1" className="font-semibold text-gray-900 text-sm">{details?.createdBy?.name || '—'}</Typography>
									</>
								)}
							</div>
						</div>

					{/* Product Image */}
							<div>
								<div className="bg-white rounded-lg flex items-center justify-center h-56 border border-gray-100 p-4">
									<div className="text-center">
								<div
									className={`w-40 h-40 bg-gray-100 rounded-lg mx-auto mb-2 flex items-center justify-center border border-gray-200 ${isEditing && !isEditInitializationLoading && !(previewImageUrl || displayImageUrl) ? 'cursor-pointer hover:bg-gray-50 hover:border-gray-300' : ''}`}
									onClick={() => {
										if (isEditing && !isEditInitializationLoading && !(previewImageUrl || displayImageUrl)) {
											fileInputRef.current?.click();
										}
									}}
								>
									{isImageLoading ? (
										<div className="w-10 h-10 rounded-full border-4 border-gray-300 border-t-[#1D5176] animate-spin" aria-label="Loading image" />
                                    ) : (previewImageUrl || displayImageUrl) ? (
                                        <img src={previewImageUrl || displayImageUrl || undefined} alt="Product" className="w-full h-full object-contain" />
									) : (
										<Typography variant="body2" className="text-gray-500 text-xs text-center">
											{isCreate ? 'Click to upload image' : 'No image provided'}
										</Typography>
									)}
								</div>
								{isEditing && (
									<div className="mt-2 flex items-center justify-center gap-2">
										<input
											ref={fileInputRef}
											type="file"
											accept="image/*"
											disabled={isEditInitializationLoading}
											onChange={(e) => {
												const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
												if (previewImageUrl) URL.revokeObjectURL(previewImageUrl);
												const localUrl = file ? URL.createObjectURL(file) : null;
												setPreviewImageUrl(localUrl);
												if (file) uploadImageImmediate(file);
											}}
											className="hidden"
										/>
										<button disabled={isEditInitializationLoading} onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 text-xs rounded border border-gray-300 disabled:cursor-not-allowed disabled:opacity-60">Upload</button>
										{uploadingImage && (
											<div className="text-[11px] text-gray-600">Uploading…</div>
										)}
										{uploadError && (
											<div className="text-[11px] text-red-600">{uploadError}</div>
										)}
									</div>
								)}
									</div>
								</div>
							</div>

                            {/* Details block: view vs edit/create layouts */}
                            {(isEditing || isCreate) ? (
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <div>
                                        <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">
                                            Format Number <span className="text-red-500">*</span>
                                        </Typography>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            maxLength={3}
                                            {...register('format')}
                                            disabled={isEditInitializationLoading}
                                            className={`w-full bg-white border rounded px-2 py-1 text-sm ${errors.format ? 'border-red-500' : 'border-gray-300'}`}
                                        />
                                        {errors.format && (
                                            <div className="text-red-500 text-xs mt-1">{errors.format.message}</div>
                                        )}
                                    </div>
                                    <div>
                                        <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">
                                            Part Number <span className="text-red-500">*</span>
                                        </Typography>
                                        <input
                                            {...register('partNumber')}
                                            disabled={isEditInitializationLoading}
                                            className={`w-full bg-white border rounded px-2 py-1 text-sm ${errors.partNumber ? 'border-red-500' : 'border-gray-300'}`}
                                            onChange={handlePartNumberChange}
                                        />
                                        {errors.partNumber && (
                                            <div className="text-red-500 text-xs mt-1">{errors.partNumber.message}</div>
                                        )}
                                    </div>
                                    <div>
                                        <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">
                                            PCL
                                        </Typography>
                                        <input
                                            {...register('pcl')}
                                            disabled={isEditInitializationLoading}
                                            className={`w-full bg-white border rounded px-2 py-1 text-sm ${errors.pcl ? 'border-red-500' : 'border-gray-300'}`}
                                        />
                                        {errors.pcl && (
                                            <div className="text-red-500 text-xs mt-1">{errors.pcl.message}</div>
                                        )}
                                    </div>
                                    <div>
                                        <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">
                                            Business Unit
                                        </Typography>
                                        <select
                                            {...register('businessUnit')}
                                            value={selectedBusinessUnit || ''}
                                            disabled={isEditInitializationLoading}
                                            className={`w-full bg-white border rounded px-2 py-1 text-sm ${errors.businessUnit ? 'border-red-500' : 'border-gray-300'}`}
                                        >
                                            <option value="">—</option>
                                            {businessUnitOptions.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                                        </select>
                                        {errors.businessUnit && (
                                            <div className="text-red-500 text-xs mt-1">{errors.businessUnit.message}</div>
                                        )}
                                    </div>
                                    <div>
                                        <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">
                                            Product Family
                                        </Typography>
                                        <select
                                            {...register('productFamily')}
                                            value={selectedProductFamily || ''}
                                            disabled={isEditInitializationLoading}
                                            className={`w-full bg-white border rounded px-2 py-1 text-sm ${errors.productFamily ? 'border-red-500' : 'border-gray-300'}`}
                                        >
                                            <option value="">—</option>
                                            {familyOptions.map(f => <option key={f} value={f}>{f}</option>)}
                                        </select>
                                        {errors.productFamily && (
                                            <div className="text-red-500 text-xs mt-1">{errors.productFamily.message}</div>
                                        )}
                                    </div>
                                    <div>
                                        <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">
                                            Product Line
                                        </Typography>
                                        <select
                                            {...register('productLine')}
                                            value={selectedProductLine || ''}
                                            disabled={isEditInitializationLoading}
                                            className={`w-full bg-white border rounded px-2 py-1 text-sm ${errors.productLine ? 'border-red-500' : 'border-gray-300'}`}
                                        >
                                            <option value="">—</option>
                                            {lineOptions.map(l => <option key={l} value={l}>{l}</option>)}
                                        </select>
                                        {errors.productLine && (
                                            <div className="text-red-500 text-xs mt-1">{errors.productLine.message}</div>
                                        )}
                                    </div>
                                    <div>
                                        <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Version</Typography>
                                        <Typography variant="body2" className="font-semibold text-gray-900">—</Typography>
                                    </div>
                                    {/* Stack Members */}
                                    <div>
                                        <div className="text-gray-500 text-xs mb-0.5">
                                            Stack members
                                        </div>
                                        <UserSelector
                                            label=""
                                            userOptions={stackUserOptions}
                                            selectedUserIds={selectedStackIds}
                                            onSelectionChange={handleStackUserSelectionChange}
                                            placeholder={stackUsersLoading ? "Loading users..." : "Select users"}
                                            width="w-full"
                                            dropdownWidth="w-full"
                                            labelClassName=""
                                            disabled={stackUsersLoading || isEditInitializationLoading}
                                        />
                                        {stackUsersError && stackUserOptions.length === 0 && (
                                            <div className="mt-2 flex items-center justify-between gap-2 rounded border border-red-200 bg-red-50 px-3 py-2">
                                                <div className="text-xs text-red-700">{stackUsersError}</div>
                                                <button
                                                    type="button"
                                                    onClick={() => void loadStackUsers()}
                                                    disabled={stackUsersLoading}
                                                    className="shrink-0 rounded border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    Retry
                                                </button>
                                            </div>
                                        )}
                                        {errors.stackMembers && (
                                            <div className="text-red-500 text-xs mt-1">{errors.stackMembers.message}</div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-gray-50 rounded-lg p-4">
                                    {/* Row 1 */}
                                    <div className="grid grid-cols-3 gap-6">
                                        <div>
                                            <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Business Unit</Typography>
                                            <Typography variant="body2" className="font-semibold text-gray-900">{details?.businessUnit || '—'}</Typography>
                                        </div>
                                        <div>
                                            <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Product Family</Typography>
                                            <Typography variant="body2" className="font-semibold text-gray-900">{details?.productFamily || '—'}</Typography>
                                        </div>
                                        <div>
                                            <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Part Number</Typography>
                                            <Typography variant="body2" className="font-semibold text-gray-900">{details?.partNumber || '—'}</Typography>
                                        </div>
                                    </div>
                                    {/* Divider */}
                                    <div className="h-px bg-gray-200 my-4" />
                                    {/* Row 2: 4 columns same line */}
                                    <div className="grid grid-cols-4 gap-6">
                                        <div>
                                            <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Product Line</Typography>
                                            <Typography variant="body2" className="font-semibold text-gray-900">{details?.productLine || '—'}</Typography>
                                        </div>
                                        <div>
                                            <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Revision</Typography>
                                            <Typography variant="body2" className="font-semibold text-gray-900">—</Typography>
                                        </div>
                                        <div>
                                            <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">Format</Typography>
                                            <Typography variant="body2" className="font-semibold text-gray-900">{details?.format_Id || '—'}</Typography>
                                        </div>
                                        <div>
                                            <Typography variant="caption" className="text-gray-500 text-xs mb-0.5">PCL</Typography>
                                            <Typography variant="body2" className="font-semibold text-gray-900">{details?.pcl || '—'}</Typography>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Read-only stacked members (compact) */}
                            {!isEditing && !isCreate && (
                                <div className="mt-2">
                                    <Typography variant="subtitle2" className="font-semibold text-gray-900 mb-2 text-sm">Stack members</Typography>
                                    <div className="flex flex-wrap gap-1">
                                        {Array.isArray(details?.stacked) && details?.stacked.length > 0 ? (
                                            details.stacked.map((m: any) => (
                                                <Chip
                                                    key={m?.id || m?.emailAddress || m?.name}
                                                    variant="outlined"
                                                    size="small"
                                                    label={m?.name || '—'}
                                                    avatar={<AvatarImage name={m?.name || 'U'} filename={m?.avatarUri || null} size={18} />}
                                                />
                                            ))
                                        ) : (
                                            <Typography variant="body2" className="text-gray-500 text-sm">—</Typography>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Accordions */}
                    <Accordion
                        disableGutters
                        sx={{ boxShadow: 'none' }}
                        expanded={descriptionExpanded}
                        onChange={(_, expanded) => setDescriptionExpanded(expanded)}
                    >
                                <AccordionSummary
                                    className="px-2"
                                    sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { margin: 0 } }}
                                    expandIcon={<ChevronDownIcon className="h-4 w-4 text-gray-500" />}>
                                    <Typography variant="subtitle2" className="font-medium text-gray-900">
                                        Description
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails className="px-2 pt-0 pb-2">
                            {isEditing ? (
                                <div>
                                    <textarea
                                        {...register('description')}
                                        disabled={isEditInitializationLoading}
                                        className={`w-full bg-white border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical min-h-[80px] ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
                                        rows={4}
                                        placeholder="Enter description..."
                                    />
                                    {errors.description && (
                                        <div className="text-red-500 text-xs mt-1">{errors.description.message}</div>
                                    )}
                                </div>
                            ) : (
                                                <Typography variant="body2" className="text-gray-700 whitespace-pre-line">{details?.description || '—'}</Typography>
                            )}
                                </AccordionDetails>
                            </Accordion>

							{/* Attached Files */}
							<div>
								<Typography variant="subtitle2" className="font-semibold text-gray-900 mb-3 text-sm">Attached Files</Typography>
								<div className="space-y-2">
									{(editAttachments || []).length === 0 ? (
										<Typography variant="body2" className="text-gray-500 text-sm">No attachments</Typography>
									) : (
										<ul className="divide-y divide-gray-200 border border-gray-200 rounded">
											{editAttachments.map((name, idx) => (
												<li key={idx} className="flex items-center justify-between px-3 py-2 text-sm">
													<span className="flex items-center gap-2 truncate">
														<Paperclip className="h-4 w-4 text-gray-500" />
														<button
															onClick={async () => {
															try {
																const { data } = await axios.get(`/File/${encodeURIComponent(name)}`, { responseType: 'blob' });
																const url = window.URL.createObjectURL(data);
																const a = document.createElement('a');
																a.href = url;
																a.download = name;
																document.body.appendChild(a);
																a.click();
																window.URL.revokeObjectURL(url);
																document.body.removeChild(a);
															} catch (err) {
																logSystemError(null, {
																	action: 'download_codesheet_attachment',
																	error: err,
																	resource: 'codesheet',
																	resourceId: recordId,
																	context: { fileName: name },
																});
																console.error('Download failed', err);
															}
														}}
															className="text-left truncate hover:underline"
															title={`Download ${name}`}
														>
															<span className="truncate inline-block max-w-[380px] align-bottom">{name}</span>
														</button>
													</span>
													<div className="flex items-center gap-1">
														<button
															onClick={async () => {
															try {
																const { data } = await axios.get(`/File/${encodeURIComponent(name)}`, { responseType: 'blob' });
																const url = window.URL.createObjectURL(data);
																const a = document.createElement('a');
																a.href = url;
																a.download = name;
																document.body.appendChild(a);
																a.click();
																window.URL.revokeObjectURL(url);
																document.body.removeChild(a);
															} catch (err) {
																logSystemError(null, {
																	action: 'download_codesheet_attachment',
																	error: err,
																	resource: 'codesheet',
																	resourceId: recordId,
																	context: { fileName: name },
																});
																console.error('Download failed', err);
															}
														}}
														className="p-1 rounded hover:bg-blue-50"
														title="Download"
													>
														<Download className="h-4 w-4 text-[#1D5176]" />
													</button>
														{isEditing && (
															<button
																onClick={() => setEditAttachments(prev => prev.filter((_, i) => i !== idx))}
																className="p-1 rounded hover:bg-gray-100"
																title="Remove attachment"
															>
																<Trash2 className="h-4 w-4 text-gray-500" />
															</button>
														)}
													</div>
												</li>
											))}
										</ul>
									)}

									{isEditing && (
										<div className="flex items-center gap-2">
											<input
												ref={attachmentsInputRef}
												type="file"
												multiple
												disabled={isEditInitializationLoading}
												onChange={async (e) => {
													const files = e.target.files ? Array.from(e.target.files) : [];
													if (!files.length) return;
													setUploadingAttachments(true);
													setAttachmentsError(null);
													try {
														const uploaded: string[] = [];
														for (const f of files) {
															const name = await uploadAttachment(f);
															uploaded.push(name);
														}
														setEditAttachments(prev => [...prev, ...uploaded]);
														if (attachmentsInputRef.current) attachmentsInputRef.current.value = '';
													} catch (err) {
														logSystemError(null, {
															action: 'upload_codesheet_attachment',
															error: err,
															resource: 'codesheet',
															resourceId: recordId,
															context: { fileCount: files.length, fileNames: files.map(file => file.name) },
														});
														setAttachmentsError('Attachment upload failed');
													} finally {
														setUploadingAttachments(false);
													}
											}}
											className="hidden"
										/>
										<button disabled={isEditInitializationLoading} onClick={() => attachmentsInputRef.current?.click()} className="px-3 py-1.5 text-xs rounded border border-gray-300 disabled:cursor-not-allowed disabled:opacity-60">Add files</button>
										{uploadingAttachments && (
											<div className="text-[11px] text-gray-600">Uploading…</div>
										)}
										{attachmentsError && (
											<div className="text-[11px] text-red-600">{attachmentsError}</div>
										)}
									</div>
									)}
								</div>
							</div>
						</div>
					)}

					{activeTab === 1 && (
						<div className="text-center py-8">
							<Typography variant="body2" className="text-gray-500">Changelog content will be displayed here</Typography>
						</div>
					)}
				</div>

                <div className="border-t border-gray-200 p-4 flex items-center justify-between gap-3 bg-white">
					<div className="text-red-600 text-xs">{error}</div>
					
					<div className="flex items-center gap-2">
							{/* Open temporarily disabled */}
							{!isCreate && !isEditing && recordId && showOpenButton && (
								<button onClick={() => navigate(`/details/${encodeURIComponent(String(recordId))}`)} className="bg-[#1D5176] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700">
									Open
								</button>
							)}

							{/* Save-Cancel when editing */}
							{isEditing ? (
							<>
									<button disabled={saving || uploadingImage || uploadingAttachments} onClick={() => setIsEditing(false)} className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 disabled:opacity-60">Cancel</button>
									<button disabled={isEditInitializationLoading || saving || uploadingImage || uploadingAttachments} onClick={handleSubmit(onSubmit)} className="bg-[#1D5176] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60">{isEditInitializationLoading ? 'Loading…' : saving ? 'Saving…' : (uploadingImage || uploadingAttachments) ? 'Uploading…' : 'Save'}</button>
							</>
							) : null}
					</div>
				</div>
			</div>
		</Drawer>
	);
}


